import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSet } from 'react-use';
import { useDebounce } from 'use-debounce';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { RawButton } from '../../../CommonComponents/OPL/Button/Button.styles';
import { Card } from '../../../CommonComponents/OPL/Card/Card';
import { Dot } from '../../../CommonComponents/OPL/Dot/Dot';
import { Cell, HtmlTable } from '../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Icon } from '../../../CommonComponents/OPL/Icon/Icon';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../CommonComponents/OPL/Text/TruncatedText';
import { SERIES_COLORS } from '../../../CommonComponents/OPL/charts/charts.constants';
import { useIsEmbeddedMobile } from '../../../_hooks/useIsEmbeddedMobile';
import { sortTableRows } from '../../helpers/sortTableRows';
import { usePerformanceDetails } from '../../hooks/usePerformanceDetails';
import { usePeriod } from '../../hooks/usePeriod';
import { useRouteParams } from '../../hooks/useRouteParams';
import {
  Sorting,
  SortingDirection,
} from '../../tabs/Positions/components/AlternativeDetail/AlternativeDetail.meta';
import { GrossOfFeeIcon } from '../GrossOfFeeIcon/GrossOfFeeIcon';
import { PerformanceDetailsEmpty } from './PerformanceDetails.empty';
import { PerformanceDetailsError } from './PerformanceDetails.error';
import { PerformanceDetailsLoading } from './PerformanceDetails.loading';
import { PerformanceTableRow } from './PerformanceDetails.styles';
import { useFilteredRows } from './hooks/useFilteredRows';
import { setGroupSelectionSelectedTabIndex } from '../../state/actions';
import { selectGroupSelectionData } from '../../state/selectors';
import { TabSlug } from '../../types';

interface Props {
  title: string;
}

export const PerformanceDetails = ({ title }: Props) => {
  const dispatch = useDispatch();

  const { accountId, clientId, groupId } = useRouteParams();

  const [, { has: isRowExpanded, toggle: toggleRow, reset }] = useSet<string>();

  const { selectedTabIndex, tabList } = useSelector(selectGroupSelectionData(TabSlug.Performance));

  const setSelectedTabIndex = (index: number) =>
    dispatch(setGroupSelectionSelectedTabIndex(index, TabSlug.Performance));

  const period = usePeriod();

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const { isFetching, isError, refetch, data } = usePerformanceDetails({
    clientId,
    period: period.value,
    startDate: period.dates[0],
    endDate: period.dates[1],
    accountId,
    groupId,
    isEmbeddedMobile,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const [sorting, setSorting] = useState<Sorting>({});

  // Reset the set when changing tabs or fetching a new range
  useEffect(() => {
    reset();
  }, [reset, selectedTabIndex, isFetching]);

  useEffect(() => {
    setSearchTerm('');
  }, [selectedTabIndex]);

  const unfilteredRows = useMemo(() => {
    if (!data?.tableData?.rows) {
      return null;
    }

    if (Object.keys(sorting).length === 0) {
      return data.tableData.rows;
    }

    const sortingKey = Object.keys(sorting)[0];

    return sortTableRows(data.tableData.rows, sortingKey, sorting[sortingKey]);
  }, [data, sorting]);

  const rows = useFilteredRows({ rows: unfilteredRows, filter: debouncedSearchTerm });

  if (isFetching) {
    return <PerformanceDetailsLoading />;
  }

  if (isError) {
    return <PerformanceDetailsError onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  const { tableData, defaultTabIndex } = data;

  if (!tabList) {
    return <PerformanceDetailsEmpty />;
  }

  if (!tableData) {
    return null;
  }

  const tabOptions = tabList.map((tab, index) => ({
    value: index,
    label: tab,
  }));

  const TableWrapper = isEmbeddedMobile ? Box : Card;

  return (
    <Box>
      {isEmbeddedMobile && (
        <Box p="16" borderBottom="1px solid" borderColor="neutral.200">
          <Text variant="cardTitle" mb="8">
            {title}
          </Text>

          <Select
            value={tabOptions.find(({ value }) => value === (selectedTabIndex ?? defaultTabIndex))}
            isSearchable={false}
            options={tabOptions}
            onChange={opt => {
              if (opt) {
                setSelectedTabIndex(opt.value);
              }
            }}
          />
        </Box>
      )}

      <TableWrapper>
        {!isEmbeddedMobile && (
          <Text variant="cardTitle" px="16" pt="12" pb="4">
            {title} <GrossOfFeeIcon />
          </Text>
        )}

        {!isEmbeddedMobile && (
          <Box p="16">
            <Input
              value={searchTerm}
              onChange={({ target: { value } }) => setSearchTerm(value)}
              placeholder="Search..."
              leftIcon="fa fa-search"
            />
          </Box>
        )}

        <HtmlTable
          variant="embedded"
          columnSpacing={tableData.headers.length > 4 || isEmbeddedMobile ? 'small' : 'medium'}
        >
          <table>
            <colgroup>
              <col width="100%" />
              <col width="0%" />
            </colgroup>

            <thead>
              <tr>
                {tableData.headers
                  .slice(...(isEmbeddedMobile ? [0, 2] : []))
                  .map((header, index) => (
                    <th key={index}>
                      <RawButton
                        onClick={() => {
                          if (sorting[index]) {
                            setSorting(
                              sorting[index] === SortingDirection.Asc
                                ? { [index]: SortingDirection.Desc }
                                : {},
                            );
                          } else {
                            setSorting({ [index]: SortingDirection.Asc });
                          }
                        }}
                        aria-label="Sort by column"
                        css="width: 100%"
                      >
                        <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                          <Text
                            variant="tableHeader"
                            textAlign={index === 0 ? 'left' : 'right'}
                            width="100%"
                          >
                            {header.text}
                          </Text>
                          {(sorting[index] === SortingDirection.Asc ||
                            sorting[index] === SortingDirection.Desc) && (
                            <Icon
                              className={
                                sorting[index] === SortingDirection.Asc
                                  ? 'fas fa-arrow-up'
                                  : 'fas fa-arrow-down'
                              }
                              color="neutral.500"
                            />
                          )}
                        </Box>
                      </RawButton>
                    </th>
                  ))}
              </tr>
            </thead>

            <tbody>
              {(rows ?? []).map(
                ({ cells, childRows, color, isBenchmark, isTotal, id }, rowIndex) => {
                  const hasChildRows = childRows && childRows.length > 0;
                  const isExpanded = id
                    ? isRowExpanded(id) || (!!searchTerm && hasChildRows)
                    : false;
                  // eslint-disable-next-line no-nested-ternary
                  const tableRowVariant = isBenchmark
                    ? 'disabled'
                    : hasChildRows
                    ? 'clickable'
                    : 'default';

                  return (
                    <React.Fragment key={`${id}-${rowIndex}`}>
                      <PerformanceTableRow
                        isExpanded={isExpanded}
                        isEmbeddedMobile={isEmbeddedMobile}
                        onClick={hasChildRows && id ? () => toggleRow(id) : undefined}
                        variant={tableRowVariant}
                      >
                        {cells.slice(...(isEmbeddedMobile ? [0, 2] : [])).map((cell, index) => (
                          <Cell key={index} isFlex={index === 0}>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent={index === 0 ? 'flex-start' : 'flex-end'}
                            >
                              {index === 0 && hasChildRows && (
                                <Icon
                                  className={
                                    isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'
                                  }
                                  color="neutral.400"
                                  mr={isExpanded ? 8 : 12}
                                />
                              )}

                              {index === 0 ? (
                                <>
                                  {!isEmbeddedMobile && !isTotal && !isBenchmark && (
                                    <Dot
                                      bg={color || SERIES_COLORS[rowIndex % SERIES_COLORS.length]}
                                      mr="8"
                                    />
                                  )}

                                  <TruncatedText
                                    fontWeight={isBenchmark ? 500 : 600}
                                    color={isBenchmark ? 'neutral.500' : 'neutral.700'}
                                    textAlign="left"
                                    width="100%"
                                  >
                                    {cell.text}
                                  </TruncatedText>
                                </>
                              ) : (
                                <Box>
                                  <Text
                                    fontWeight={isBenchmark ? 500 : 600}
                                    color={isBenchmark ? 'neutral.500' : 'neutral.700'}
                                    textAlign="right"
                                    width="100%"
                                    variant="tabular"
                                  >
                                    {cell.text}
                                  </Text>

                                  {isEmbeddedMobile && cells[2] && (
                                    <Text
                                      fontWeight={isBenchmark ? 500 : 600}
                                      fontSize="small"
                                      color="neutral.500"
                                      textAlign="right"
                                      width="100%"
                                      variant="tabular"
                                    >
                                      {cells[2].text}
                                    </Text>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Cell>
                        ))}
                      </PerformanceTableRow>

                      {isExpanded &&
                        childRows &&
                        childRows.map((childRow, key) => (
                          <PerformanceTableRow
                            key={key}
                            variant={childRow.isBenchmark ? 'disabled' : 'default'}
                            isEmbeddedMobile={isEmbeddedMobile}
                          >
                            {childRow.cells
                              .slice(...(isEmbeddedMobile ? [0, 2] : []))
                              .map((childCell, index) => (
                                <Cell key={index} isFlex={index === 0}>
                                  {index === 0 ? (
                                    <TruncatedText
                                      fontWeight={childRow.isBenchmark ? 500 : 400}
                                      color={childRow.isBenchmark ? 'neutral.500' : 'neutral.700'}
                                      textAlign="left"
                                      width="100%"
                                      pl={isEmbeddedMobile ? 24 : 40}
                                    >
                                      {childCell.text}
                                    </TruncatedText>
                                  ) : (
                                    <>
                                      <Text
                                        fontWeight={childRow.isBenchmark ? 500 : 400}
                                        color={childRow.isBenchmark ? 'neutral.500' : 'neutral.700'}
                                        textAlign="right"
                                        width="100%"
                                        variant="tabular"
                                      >
                                        {childCell.text}
                                      </Text>

                                      {isEmbeddedMobile && childRow.cells[2] && (
                                        <Text
                                          fontWeight={childRow.isBenchmark ? 500 : 400}
                                          fontSize="small"
                                          color="neutral.500"
                                          textAlign="right"
                                          width="100%"
                                          variant="tabular"
                                        >
                                          {childRow.cells[2].text}
                                        </Text>
                                      )}
                                    </>
                                  )}
                                </Cell>
                              ))}
                          </PerformanceTableRow>
                        ))}
                    </React.Fragment>
                  );
                },
              )}
            </tbody>
          </table>
        </HtmlTable>
      </TableWrapper>
    </Box>
  );
};
